import React, { useEffect, useState } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GroupsIcon from "@mui/icons-material/Groups";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AuthService from "../../services/AuthService";
import UserService from "../../services/UserService";
import { trackEvent } from "../../services/GoogleAnalytics";
import { ACCENT, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import { hasUserRole } from "../../admin/account/userRoles";
import { isTutorStandardUser } from "../../services/PayPlanUtil";
import TutorsDashboardPage from "./TutorsDashboardPage";

export default function TutorsLandingPage() {
    const { t } = usePagesTranslation();
    const [hasHubAccess, setHasHubAccess] = useState(false);

    useEffect(() => {
        trackEvent("entered", { name: "TutorsLandingPage" });
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const auth = await AuthService.isAuthenticated();
                if (!auth) {
                    return;
                }
                const user = await UserService.getUserInfo();
                if (!cancelled) {
                    const cloudUser = user?.cloudUser;
                    setHasHubAccess(hasUserRole(cloudUser, "TUTOR") || isTutorStandardUser(cloudUser));
                }
            } catch {
                if (!cancelled) {
                    setHasHubAccess(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const benefits = [
        {
            key: "paste",
            icon: <EditNoteIcon sx={{ fontSize: 32, color: ACCENT.main }} />,
            title: t("tutors.benefit-paste-title", "Paste text, draft a crossword"),
            body: t(
                "tutors.benefit-paste-body",
                "Write or paste a short lesson. We draft a crossword; you place some of the words. Private to you — not on the public puzzle lists.",
            ),
        },
        {
            key: "groups",
            icon: <GroupsIcon sx={{ fontSize: 32, color: ACCENT.main }} />,
            title: t("tutors.benefit-groups-title", "Groups for classes"),
            body: t(
                "tutors.benefit-groups-body",
                "Create groups (classes) and attach your private crosswords. Not courses.",
            ),
        },
        {
            key: "print",
            icon: <PictureAsPdfIcon sx={{ fontSize: 32, color: ACCENT.main }} />,
            title: t("tutors.benefit-print-title", "Printable worksheets"),
            body: t(
                "tutors.benefit-print-body",
                "Download a PDF: puzzle page, then answer page with each word and meaning. Organization name and logo on the header.",
            ),
        },
    ];

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", py: { xs: 3, sm: 4 } }}>
            <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 } }}>
                <Typography
                    variant="overline"
                    sx={{ display: "block", textAlign: "center", letterSpacing: 1.2, color: ACCENT.main, fontWeight: 700 }}
                >
                    {t("tutors.badge", "For tutors")}
                </Typography>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        textAlign: "center",
                        mb: 1.5,
                        fontWeight: 700,
                        color: "#3BA666",
                        fontSize: { xs: "1.75rem", sm: "2.25rem" },
                    }}
                >
                    {t("tutors.headline", "Private crossword worksheets for tutoring")}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        textAlign: "center",
                        mb: 2,
                        color: TEXT.secondary,
                        maxWidth: 640,
                        mx: "auto",
                        fontWeight: 600,
                    }}
                >
                    {t(
                        "tutors.subtitle",
                        "Paste text, draft a crossword, group it for a class, and print a branded two-page PDF.",
                    )}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                        maxWidth: 760,
                        mx: "auto",
                        mb: 3,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: ACCENT.main,
                        bgcolor: "rgba(59, 166, 102, 0.08)",
                        animation: "tutorCreateGlow 2.8s ease-in-out infinite",
                        "@keyframes tutorCreateGlow": {
                            "0%, 100%": { boxShadow: "0 0 0 0 rgba(59, 166, 102, 0.12)" },
                            "50%": { boxShadow: "0 0 24px 3px rgba(59, 166, 102, 0.32)" },
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                        },
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 36, color: ACCENT.main, mt: 0.25, flexShrink: 0 }} aria-hidden />
                    <Box>
                        <Typography variant="h6" component="p" sx={{ fontWeight: 800, color: TEXT.primary, lineHeight: 1.3 }}>
                            {t("tutors.try-title", "Try creating your puzzle. No cost.")}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: ACCENT.main, mt: 0.5 }}>
                            {t("tutors.try-hook", "Put your text in and generate. That's it!")}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1.25, color: TEXT.secondary, lineHeight: 1.7 }}>
                            {t(
                                "tutors.try-detail",
                                "Write or paste a lesson, a paragraph, or the words you want to teach. We draft a private crossword from that text. Play it in the browser, attach it to a class group, or print a two-page worksheet with the puzzle and the answers. Creating and trying a puzzle does not add a charge, and it stays off the public puzzle lists.",
                            )}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, flexWrap: "wrap", mb: 4 }}>
                    <Button
                        component={RouterLink}
                        to="/tutors/library#create"
                        variant="contained"
                        sx={{ minHeight: 44, px: 3, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                    >
                        {t("tutors.create-story", "Create a story")}
                    </Button>
                    <Button
                        component={RouterLink}
                        to="/support"
                        variant="outlined"
                        sx={{ minHeight: 44, px: 3, textTransform: "none", fontWeight: 700 }}
                    >
                        {t("nav.tutors-support", "Support")}
                    </Button>
                </Box>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {benefits.map((item) => (
                        <Grid key={item.key} size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
                            <Card sx={{ p: 2.5, flex: 1, width: "100%", boxShadow: 1 }}>
                                <Box sx={{ mb: 1.25 }}>{item.icon}</Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75, color: TEXT.primary }}>
                                    {item.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {item.body}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {hasHubAccess ? <TutorsDashboardPage embedded /> : null}
            </Box>
        </Box>
    );
}
